// ============================================
// EXPOKIOSKO.JS — Pantalla kiosko para tableta (Expo Alianza)
// Pública: sin login. Solo QR que apunta a la landing de leads.
// ============================================

import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const PP = "'Segoe UI', 'Poppins', sans-serif";

const COLORES = {
  fondo: '#5e99d2',
  acento: '#dcde74',
  azulClaro: '#8fb8e8',
};

const URL_LANDING = 'https://beep-os-production-5a7b.up.railway.app/expo-leads.html';

export default function ExpoKiosko() {
  const [ancho, setAncho] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [enFullscreen, setEnFullscreen] = useState(false);
  const [soportaFullscreen] = useState(
    typeof document !== 'undefined' && !!document.documentElement.requestFullscreen
  );

  useEffect(() => {
    const onResize = () => setAncho(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onFsChange = () => setEnFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen();
  };

  const esMovil = ancho < 768;
  const qrSize = esMovil ? 240 : 320;

  return (
    <div style={{
      minHeight: '100vh',
      minHeight: '100dvh',
      background: `radial-gradient(ellipse at top, #7aacd9 0%, ${COLORES.fondo} 60%, #3d7eb8 100%)`,
      fontFamily: PP,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: esMovil ? 28 : 40,
      padding: esMovil ? '24px 16px' : '40px 30px',
      boxSizing: 'border-box',
      overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Grid sutil de fondo */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `linear-gradient(rgba(220,222,116,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(220,222,116,0.06) 1px, transparent 1px)`,
        backgroundSize: '42px 42px',
        pointerEvents: 'none',
      }} />

      <style>{`
        @keyframes kiosko-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(220,222,116,0.55), 0 20px 60px rgba(0,0,0,0.3); }
          50%     { transform: scale(1.015); box-shadow: 0 0 0 24px rgba(220,222,116,0), 0 20px 60px rgba(0,0,0,0.3); }
        }
        @keyframes kiosko-fade {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes kiosko-spin1 { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes kiosko-spin2 { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
      `}</style>

      {/* ============ BOTÓN FULLSCREEN ============ */}
      {soportaFullscreen && (
        <button
          onClick={toggleFullscreen}
          title={enFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
          style={{
            position: 'fixed', top: 12, right: 12, zIndex: 999,
            background: 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: 8, padding: '6px 10px',
            color: '#fff', fontSize: 16, cursor: 'pointer',
            fontFamily: PP, backdropFilter: 'blur(4px)',
          }}
        >{enFullscreen ? '✕' : '⛶'}</button>
      )}

      {/* ============ LOGO con anillos giratorios ============ */}
      <div style={{
        position: 'relative', zIndex: 1,
        width: 130, height: 130,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'kiosko-fade 0.7s ease',
      }}>
        {/* Anillo exterior — sentido horario, 10s, dot amarillo */}
        <div style={{
          position: 'absolute', width: 130, height: 130,
          border: `2px solid rgba(220,222,116,0.4)`,
          borderRadius: '50%',
          animation: 'kiosko-spin1 10s linear infinite',
        }}>
          <div style={{
            position: 'absolute', top: -6, left: 53,
            width: 12, height: 12, borderRadius: '50%',
            background: COLORES.acento,
          }} />
        </div>
        {/* Anillo interior — sentido antihorario, 14s, dot azul claro */}
        <div style={{
          position: 'absolute', width: 106, height: 106,
          border: `1.5px solid rgba(143,184,232,0.3)`,
          borderRadius: '50%',
          animation: 'kiosko-spin2 14s linear infinite',
        }}>
          <div style={{
            position: 'absolute', bottom: -5, right: 14,
            width: 10, height: 10, borderRadius: '50%',
            background: COLORES.azulClaro,
          }} />
        </div>
        {/* Logo central */}
        <img
          src="/icono azul beep.png"
          alt="Beep México"
          style={{ width: 78, height: 78, objectFit: 'contain', position: 'relative', zIndex: 2 }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      </div>

      {/* ============ QR responsive ============ */}
      <div style={{
        position: 'relative', zIndex: 1,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        animation: 'kiosko-fade 0.9s ease',
      }}>
        <div style={{
          background: '#fff',
          padding: 32,
          borderRadius: 24,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          animation: 'kiosko-pulse 2.4s ease-in-out infinite',
        }}>
          <QRCodeSVG
            value={URL_LANDING}
            size={qrSize}
            level="H"
            bgColor="#ffffff"
            fgColor={COLORES.fondo}
            includeMargin={false}
          />
        </div>
      </div>

      {/* ============ TEXTO ============ */}
      <div style={{
        position: 'relative', zIndex: 1,
        textAlign: 'center',
        animation: 'kiosko-fade 1.1s ease',
      }}>
        <div style={{
          fontSize: esMovil ? 22 : 28, fontWeight: 800, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
          marginBottom: 8,
        }}>
          <span style={{ fontSize: esMovil ? 26 : 32 }}>📱</span>
          Escanea para recibir información
        </div>
        <div style={{
          fontSize: esMovil ? 12 : 14, color: COLORES.acento,
          letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600,
        }}>
          beepmexico.com
        </div>
      </div>
    </div>
  );
}
